import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";

export interface FrankLogEntry {
  correlationId: string;
  organizationId?: string;
  memberId?: string;
  associationId?: number;
  question?: string;
  language?: string;
  apiUrl?: string;
  threshold?: number;
  success: boolean;
  responseTimeMs?: number;
  answer?: string;
  sources?: any;
  errorType?: string;
  errorCode?: number;
  errorMessage?: string;
  errorDetails?: any;
  userAgent?: string;
  isMobile?: boolean;
  networkType?: string;
}

export class FrankLogger {
  private static instance?: FrankLogger;
  private correlationId: string = uuidv4();

  static getInstance(): FrankLogger {
    if (!FrankLogger.instance) {
      FrankLogger.instance = new FrankLogger();
    }
    return FrankLogger.instance;
  }

  /**
   * Generate a new correlation ID for tracking related operations
   */
  generateCorrelationId(): string {
    this.correlationId = uuidv4();
    return this.correlationId;
  }

  /**
   * Get the current correlation ID
   */
  getCurrentCorrelationId(): string {
    return this.correlationId;
  }

  /**
   * Log Frank interaction to database
   */
  async logInteraction(entry: FrankLogEntry): Promise<void> {
    try {
      // Get network information if available
      const nav = navigator as any;
      const networkType = nav.connection?.effectiveType || nav.connection?.type || 'unknown';
      
      const logData = {
        correlation_id: entry.correlationId,
        organization_id: entry.organizationId || null,
        member_id: entry.memberId || null,
        association_id: entry.associationId || null,
        question: entry.question || null,
        language: entry.language || 'en',
        api_url: entry.apiUrl || null,
        threshold: entry.threshold || null,
        success: entry.success,
        response_time_ms: entry.responseTimeMs || null,
        answer: entry.answer || null,
        sources: entry.sources ? JSON.stringify(entry.sources) : null,
        error_type: entry.errorType || null,
        error_code: entry.errorCode || null,
        error_message: entry.errorMessage || null,
        error_details: entry.errorDetails ? JSON.stringify(entry.errorDetails) : null,
        user_agent: entry.userAgent || navigator.userAgent,
        is_mobile: entry.isMobile || window.innerWidth < 768,
        network_type: entry.networkType || networkType,
      };

      const { error } = await supabase
        .from('frank_interaction_logs')
        .insert(logData);

      if (error) {
        console.error('Failed to log Frank interaction:', error);
        // Don't throw error to avoid breaking the main flow
      } else {
        console.log('Frank interaction logged successfully:', entry.correlationId);
      }
    } catch (error) {
      console.error('Error logging Frank interaction:', error);
      // Don't throw error to avoid breaking the main flow
    }
  }

  /**
   * Log successful Frank response
   */
  async logSuccess(params: {
    correlationId: string;
    organizationId?: string;
    memberId?: string;
    associationId?: number;
    question: string;
    language: string;
    apiUrl: string;
    threshold: number;
    responseTimeMs: number;
    answer: string;
    sources?: any;
  }): Promise<void> {
    await this.logInteraction({
      ...params,
      success: true,
    });
  }

  /**
   * Log Frank error
   */
  async logError(params: {
    correlationId: string;
    organizationId?: string;
    memberId?: string;
    associationId?: number;
    question?: string;
    language?: string;
    apiUrl?: string;
    threshold?: number;
    responseTimeMs?: number;
    errorType: string;
    errorCode?: number;
    errorMessage: string;
    errorDetails?: any;
  }): Promise<void> {
    await this.logInteraction({
      ...params,
      success: false,
    });
  }

  /**
   * Get error type from error object
   */
  categorizeError(error: any, response?: Response): string {
    // Handle response-based errors first (when error might be null)
    if (response) {
      if (response.status === 422) return 'INVALID_REQUEST';
      if (response.status === 500) return 'API_DOWN';
      if (response.status === 403) return 'AUTH_FAILURE';
      if (response.status === 404) return 'API_NOT_FOUND';
      if (response.status >= 400) return 'API_ERROR';
    }
    
    // Check if error exists before accessing properties
    if (!error) {
      return response ? 'API_ERROR' : 'UNKNOWN_ERROR';
    }
    
    // Check error properties safely
    if (error?.name === 'AbortError') {
      return 'TIMEOUT';
    }
    
    if (error instanceof TypeError && error?.message === 'Failed to fetch') {
      return 'NETWORK_ERROR';
    }
    
    if (error?.message?.includes('CORS')) {
      return 'CORS_ERROR';
    }
    
    if (error?.message?.includes('association ID')) {
      return 'INVALID_CONFIG';
    }
    
    return 'UNKNOWN_ERROR';
  }
}

// Export singleton instance
export const frankLogger = FrankLogger.getInstance();